/**
 * API Configuration
 * Central configuration for backend API communication
 */

export const API_CONFIG = {
  // Use proxied path (via Next.js rewrites) for same-origin requests
  // This enables HttpOnly cookies to work properly
  // /backend-api/* → proxied to → http://localhost:8020/api/*
  // In production, this should be the actual API domain
  baseURL: process.env.NEXT_PUBLIC_API_URL || '/backend-api/v1',
  timeout: parseInt(process.env.NEXT_PUBLIC_API_TIMEOUT || '30000'),
  headers: {
    'Content-Type': 'application/json',
  },
};

export const API_ENDPOINTS = {
  // Workspace endpoints
  workspace: {
    list: '/workspaces',
    create: '/workspaces',
    update: (id: string) => `/workspaces/${id}`,  // PATCH /{id}
    delete: (id: string) => `/workspaces/${id}`,  // DELETE /{id}
    getById: (id: string) => `/workspaces/${id}`,
  },

  // Project endpoints
  project: {
    list: '/projects',
    create: '/projects',
    update: (id: string) => `/projects/${id}`,  // PATCH /{id}
    delete: (id: string) => `/projects/${id}`,  // DELETE /{id}
    getById: (id: string) => `/projects/${id}`,
    tree: (id: string) => `/projects/${id}/tree`,
    breadcrumb: (id: string) => `/projects/${id}/breadcrumb`,
    move: (id: string) => `/projects/${id}/move`,
    clone: (id: string) => `/projects/${id}/clone`,
    limit: '/projects/limit',
  },

  // Workflow endpoints
  workflow: {
    list: '/workflows',
    agentEntries: '/workflows/agent-entries',
    create: '/workflows',
    update: (id: string) => `/workflows/${id}`,  // PATCH /{id}
    delete: (id: string) => `/workflows/${id}`,  // DELETE /{id}
    clone: '/workflows/clone',
    addNode: '/workflows/node',
    updateNode: '/workflows/node',
    deleteNode: '/workflows/node',
    checkOut: (id: string) => `/workflows/${id}/check-out`,
    checkIn: (id: string) => `/workflows/${id}/check-in`,
    discard: (id: string) => `/workflows/${id}/discard`,
    getById: (id: string) => `/workflows/${id}`,
    // Execution endpoints
    execute: (id: string) => `/workflows/${id}/execute`,  // POST - Start workflow execution
    runs: (id: string) => `/workflows/${id}/runs`,  // GET - List workflow runs
    runDetails: (runId: string) => `/workflow_runs/${runId}`,  // GET - Get run details
    runLogs: (runId: string) => `/workflows/runs/${runId}/logs`, // GET - Get execution logs
    cancelRun: (runId: string) => `/workflow_runs/${runId}/cancel`,  // POST - Cancel running workflow
    nodeRunDetails: (runId: string) => `/workflow_runs/${runId}/nodes`,  // GET - Get node execution details
    preflight: (workflowId: string) => `/workflows/${workflowId}/preflight`,  // POST - Pre-flight connectivity checks
  },

  // Connector endpoints
  connector: {
    list: '/connectors',
    create: '/connectors',
    update: (id: string) => `/connectors/${id}`,  // PATCH /{id}
    delete: (id: string) => `/connectors/${id}`,  // DELETE /{id}
    getById: (id: string) => `/connectors/${id}`,
    testConnection: '/connectors/test-connection',  // POST - Test connection before saving
    testSavedConnection: (id: string) => `/connectors/${id}/test-connection`,  // POST - Test a saved connector
  },
  
  // Authentication endpoints (matching backend /auth routes)
  auth: {
    login: '/auth/login',           // POST - Login with email/password/tenant_id
    logout: '/auth/logout',         // POST - Logout current user
    refresh: '/auth/refresh',       // POST - Refresh access token (rotates refresh token)
    me: '/auth/me',                 // GET - Get current user info with permissions
    profile: '/auth/profile',       // GET - Get profile, PATCH - Update profile
    profileLoginHistory: '/auth/profile/login-history',  // GET - Login history
    passwordChange: '/auth/password/change',             // POST - Change password
    forgotPassword: '/auth/forgot-password',             // POST - Request password reset (always generic response)
    resetPassword: '/auth/reset-password',                // POST - Complete password reset with token
    sessions: '/auth/sessions',                                    // GET - List current user's active sessions
    session: (id: string) => `/auth/sessions/${id}`,               // DELETE - Revoke a specific session
    sessionsRevokeAll: '/auth/sessions/revoke-all',                // POST - Revoke all sessions except the current one
    register: '/auth/users',        // POST - Create new user (admin only)
    // User management
    users: '/auth/users',                                    // GET - List users, POST - Create user
    userStats: '/auth/users/stats',                          // GET - User aggregate stats
    user: (id: string) => `/auth/users/${id}`,               // GET, PATCH, DELETE user
    userRoles: (id: string) => `/auth/users/${id}/roles`,    // GET - list, POST - Assign role to user
    userRole: (id: string, roleId: string) => `/auth/users/${id}/roles/${roleId}`,  // DELETE - revoke role from user
    userPermissions: (id: string) => `/auth/users/${id}/permissions`,  // GET - Get user permissions
    userAccessSummary: (id: string) => `/auth/users/${id}/access-summary`,  // GET - effective access explorer (roles+groups+ACL+why)
    userAvatar: (id: string) => `/auth/users/${id}/avatar`,  // POST upload, GET serve, DELETE remove (profile photo)
    userApplyPersona: (id: string) => `/auth/users/${id}/apply-persona`,  // POST - grant a persona's roles to a user
    objectPermission: '/permissions/object',                 // POST - grant object-level access (user/group on workspace/project/…)
    policyRules: '/permissions/rules',                        // GET list, POST create - dynamic policy rules (conditions/effect)
    policyRule: (id: string) => `/permissions/rules/${id}`,   // GET, PATCH, DELETE a policy rule
    objectHistory: (type: string, id: string) => `/auth/history/${type}/${id}`,  // GET - row-level change history (who changed what)
    // Access reviews / certifications (governance)
    accessReviews: '/governance/access-reviews',                  // GET list, POST create
    accessReviewItems: (id: string) => `/governance/access-reviews/${id}/items`, // GET items
    accessReviewDecide: (campaignId: string, itemId: string) => `/governance/access-reviews/${campaignId}/items/${itemId}`, // PATCH
    // Group management
    groups: '/auth/groups',                                  // GET - List groups, POST - Create group
    groupStats: '/auth/groups/stats',                        // GET - Group aggregate stats
    group: (id: string) => `/auth/groups/${id}`,             // GET, PATCH, DELETE group
    groupMembers: (id: string) => `/auth/groups/${id}/members`,  // GET - List, POST - Add member
    personaTemplates: '/auth/persona-templates',             // GET - persona catalog
    groupApplyPersona: (id: string) => `/auth/groups/${id}/apply-persona`, // POST - grant persona's roles
    groupRoles: (id: string) => `/auth/groups/${id}/roles`,  // GET - list, POST - assign role to group
    groupRole: (groupId: string, roleId: string) => `/auth/groups/${groupId}/roles/${roleId}`,  // DELETE - revoke role from group
    groupMember: (groupId: string, userId: string) => `/auth/groups/${groupId}/members/${userId}`,  // DELETE - Remove member
    groupChildren: (id: string) => `/auth/groups/${id}/children`,  // GET - List, POST - Add child
    groupChild: (groupId: string, childId: string) => `/auth/groups/${groupId}/children/${childId}`,  // DELETE - Remove child
    groupParents: (id: string) => `/auth/groups/${id}/parents`,  // GET - List parent groups
    groupRules: (id: string) => `/auth/groups/${id}/rules`,  // GET - List, PUT - Set rules
    groupRule: (groupId: string, ruleId: string) => `/auth/groups/${groupId}/rules/${ruleId}`,  // DELETE - Remove rule
    groupRulesPreview: (id: string) => `/auth/groups/${id}/rules/preview`,  // POST - Preview matching users
    // Service account management
    serviceAccounts: '/auth/service-accounts',               // GET - List, POST - Create
    serviceAccount: (id: string) => `/auth/service-accounts/${id}`,  // PATCH - Update, DELETE - Soft delete
    serviceAccountStats: '/auth/service-accounts/stats',     // GET - Summary stats for dashboard cards
    // API key management
    apiKeys: '/auth/api-keys',                               // GET - List, POST - Create
    apiKey: (id: string) => `/auth/api-keys/${id}`,          // GET, PATCH, DELETE api key
    // Roles management
    roles: '/auth/roles',                                    // GET - List, POST - Create
    role: (id: string) => `/auth/roles/${id}`,               // GET - Get role details
    systemRoles: '/auth/system-roles',                       // GET - List system roles grouped
    // Role permissions management
    rolePermissions: (roleId: string) => `/auth/roles/${roleId}/permissions`,  // POST - Add permissions
    rolePermission: (roleId: string, permId: string) => `/auth/roles/${roleId}/permissions/${permId}`,  // DELETE
    // Resource bindings
    resourceBindings: '/auth/resource-bindings',             // GET - List resource-level bindings
    resourceBinding: (id: string) => `/auth/resource-bindings/${id}`,  // DELETE - Revoke binding
    // Permissions
    permissions: '/auth/permissions',                        // GET - List all permissions catalog
    permissionGrant: '/auth/permissions/grant',              // POST - Grant permission
    permissionRevoke: '/auth/permissions/revoke',            // POST - Revoke permission
    permissionCheck: '/auth/permissions/check',              // POST - Check permission
    // Tenant Policies (card-based policies UI)
    policies: '/auth/policies',                               // GET - List, POST - Create
    policy: (id: string) => `/auth/policies/${id}`,           // PATCH - Update, DELETE - Soft delete
    policyToggle: (id: string) => `/auth/policies/${id}/toggle`,  // PATCH - Toggle enabled
    // Audit Log
    auditLogs: '/auth/audit-logs',                            // GET - List audit log entries
    // Audit Logs2 — the same trail, with each event's object named and its
    // field-level before/after spelled out (see backend api/v1/audit_view.py).
    audit2Events: '/auth/audit2/events',                      // GET - Cursor-paged events with diffs
    audit2Object: (type: string, id: string) => `/auth/audit2/objects/${type}/${id}`,  // GET - One object's whole history
    audit2Summary: '/auth/audit2/summary',                    // GET - Counts, facets and coverage
    // SSO / Identity Providers (Phase 4)
    ssoProvidersAdmin: '/auth/sso/admin/providers',              // GET - full provider configs (admin)
    ssoProviders: '/auth/sso/providers',                         // GET (login page), POST - create
    ssoProvider: (id: string) => `/auth/sso/providers/${id}`,    // PATCH, DELETE
    scimConfig: '/auth/sso/scim/config',                         // GET - SCIM status + base URL
    scimToken: '/auth/sso/scim/token',                           // POST - mint/rotate, DELETE - disable
    // Resource Sharing
    allShares: '/auth/shares',                                   // GET - List all shares (admin/user)
    sharedWithMe: '/auth/resources/shared-with-me',              // GET - Object IDs shared with the current user (ACL)
    shareResource: (type: string, id: string) => `/auth/resources/${type}/${id}/share`,  // POST
    resourceAccess: (type: string, id: string) => `/auth/resources/${type}/${id}/access`, // GET
    revokeShare: (type: string, id: string, shareId: string) => `/auth/resources/${type}/${id}/share/${shareId}`, // DELETE
    // Ownership Transfer
    ownershipTransfers: '/auth/ownership-transfers',           // GET - List, POST - Initiate
    acceptTransfer: (id: string) => `/auth/ownership-transfers/${id}/accept`,  // POST
    rejectTransfer: (id: string) => `/auth/ownership-transfers/${id}/reject`,  // POST
    cancelTransfer: (id: string) => `/auth/ownership-transfers/${id}/cancel`,  // POST
    bulkTransfer: (userId: string) => `/auth/users/${userId}/transfer-all-ownership`, // POST
  },

  // GDPR / Data Privacy endpoints
  gdpr: {
    tenantExport: (tenantId: string) => `/tenants/${tenantId}/export`,  // GET - stream NDJSON export of all tenant data (StreamingResponse, not a job)
  },

  // WebSocket authentication endpoints
  ws: {
    ticket: '/ws/ticket',           // POST - Get short-lived ticket for WebSocket auth
  },

  // User Preferences endpoints
  userPreferences: {
    get: '/user-preferences',                              // GET - Get user preferences
    update: '/user-preferences',                           // PATCH - Update preferences
    favorites: '/user-preferences/favorites',               // GET - Get favorite nodes
    toggleFavorite: (nodeType: string) => `/user-preferences/favorites/${nodeType}`,  // POST - Toggle favorite
    recentNodes: '/user-preferences/recent-nodes',          // GET - Get recent nodes
    recordUsage: (nodeType: string) => `/user-preferences/record-usage/${nodeType}`,  // POST - Record usage
    frequentNodes: '/user-preferences/frequent-nodes',      // GET - Get frequent nodes
    nodeSidebar: '/user-preferences/node-sidebar',          // GET - Combined favorites + recent
    context: '/user-preferences/context',                    // GET/PATCH - User context (last ws/project, models, theme)
  },

  // Node type metadata endpoints
  nodes: {
    list: '/nodes',
    create: '/nodes',
    update: (id: string) => `/nodes/${id}`,
    delete: (id: string) => `/nodes/${id}`,
    getById: (id: string) => `/nodes/${id}`,
    typesMetadata: '/nodes/types/metadata',                 // GET - Get all node type metadata
    typeMetadata: (category: string) => `/nodes/types/${category}/metadata`,  // GET - Get specific type metadata
  },

  // MCP Server endpoints (server management)
  mcpServers: {
    // Backend declares this route as `@router.get("/")`, so the canonical
    // URL is `/mcp-servers/` (with trailing slash). Calling `/mcp-servers`
    // (no slash) returns 404 — FastAPI's redirect doesn't fire under our
    // Next rewrite proxy.
    list: '/mcp-servers/',                                 // GET - List all MCP servers
    create: '/mcp-servers',                                // POST - Create MCP server
    getById: (id: string) => `/mcp-servers/${id}`,          // GET - Get server by ID
    update: (id: string) => `/mcp-servers/${id}`,           // PATCH - Update server
    delete: (id: string) => `/mcp-servers/${id}`,           // DELETE - Delete server
    discover: (id: string) => `/mcp-servers/${id}/discover`, // POST - Discover tools
    callTool: (serverId: string, toolName: string) => `/mcp-servers/${serverId}/tools/${toolName}/call`,  // POST - Call tool
  },

  // MCP Operations endpoints (proxy to nueconn service for 100+ MCP server operations)
  mcpOperations: {
    health: '/mcp/health',                                  // GET - Check nueconn health
    listServers: '/mcp/servers',                            // GET - List all server types
    getServer: (serverType: string) => `/mcp/servers/${serverType}`,  // GET - Get server type details
    listOperations: (serverType: string) => `/mcp/${serverType}/operations`,  // GET - List operations
    execute: (serverType: string, operation: string) => `/mcp/${serverType}/operations/${operation}`,  // POST - Execute operation
  },

  // File upload endpoints (workflow node file uploads to GCS)
  files: {
    // Use local Next.js API route for uploads to handle larger files (up to 10MB)
    // The backend proxy (rewrites) has a 1MB limit, so we use a custom API route
    upload: '/api/files/upload',                            // POST - Upload file via Next.js API route
    list: (workflowId: string, nodeId: string) => `/files/list/${workflowId}/${nodeId}`,  // GET - List files (complete collection)
    // GET - one bounded page. A SEPARATE operation: `list` still returns the
    // whole collection, because the node's saved config is rebuilt from what
    // this panel loaded, and a first-page-only answer would drop the rest.
    listPage: (workflowId: string, nodeId: string) => `/files/list-page/${workflowId}/${nodeId}`,
    delete: (workflowId: string, nodeId: string, fileId: string) => `/files/${workflowId}/${nodeId}/${fileId}`,  // DELETE - Delete file
    download: (workflowId: string, nodeId: string, fileId: string) => `/files/download/${workflowId}/${nodeId}/${fileId}`,  // GET - Get signed download URL
  },

  // Run artifacts + share tokens (Agent Run Closure Phase 1 — maple.agent_artifacts)
  artifacts: {
    listForRun: (runId: string) => `/runs/${runId}/artifacts`,             // GET - List run deliverables
    download: (artifactId: string) => `/artifacts/${artifactId}/download`, // GET - Signed URL (file/page) or inline payload (data/widget)
    shareArtifact: (artifactId: string) => `/artifacts/${artifactId}/share`, // POST - Mint public token for one artifact
    shareRun: (runId: string) => `/runs/${runId}/share`,                   // POST - Mint public token for the run result page
    listTokens: (runId: string) => `/runs/${runId}/share-tokens`,          // GET - List share tokens for a run
    revokeToken: (tokenId: string) => `/share-tokens/${tokenId}`,          // DELETE - Revoke a share token
    resolveShare: (token: string) => `/pub/share/${token}`,                // GET - PUBLIC resolve (no auth)
  },

  // Document parser endpoints (RAG pipeline document parsing)
  documentParser: {
    parse: '/document-parser/parse',                        // POST - Parse single document from GCS
    parseMultiple: '/document-parser/parse-multiple',       // POST - Parse multiple documents from GCS
    parseFromNode: '/document-parser/parse-from-node',      // POST - Parse files from File Upload node
    parseUrl: '/document-parser/parse-url',                 // POST - Parse document from URL
    parseUrls: '/document-parser/parse-urls',               // POST - Parse multiple documents from URLs
    formats: '/document-parser/formats',                    // GET - List supported formats
    preview: '/document-parser/preview',                    // POST - Preview document (first 5 chunks)
  },

  // Text splitter endpoints (RAG pipeline text chunking)
  textSplitter: {
    split: '/text-splitter/split',                          // POST - Split single text into chunks
    splitMultiple: '/text-splitter/split-multiple',         // POST - Split multiple texts
    splitFromParser: '/text-splitter/split-from-parser',    // POST - Split Document Parser output
    strategies: '/text-splitter/strategies',                // GET - List available strategies
    preview: '/text-splitter/preview',                      // POST - Preview split (first 5 chunks)
  },

  // Embeddings endpoints (RAG pipeline text-to-vector conversion)
  embeddings: {
    embed: '/embeddings/embed',                             // POST - Embed single text
    embedBatch: '/embeddings/embed-batch',                  // POST - Embed multiple texts
    embedFromSplitter: '/embeddings/embed-from-splitter',   // POST - Embed Text Splitter output
    models: '/embeddings/models',                           // GET - List available models
    model: (modelId: string) => `/embeddings/models/${modelId}`,  // GET - Get model details
    estimateCost: '/embeddings/estimate-cost',              // POST - Estimate embedding cost
    preview: '/embeddings/preview',                         // POST - Preview embeddings
    clearCache: '/embeddings/clear-cache',                  // POST - Clear embedding cache
  },

  // Vector store endpoints (RAG pipeline vector database writes)
  vectorStore: {
    write: '/vector-store/write',                           // POST - Write vectors to store
    writeFromEmbeddings: '/vector-store/write-from-embeddings',  // POST - Write from Embeddings output
    collectionInfo: '/vector-store/collection-info',        // POST - Get collection info
    deleteCollection: '/vector-store/delete-collection',    // POST - Delete collection
    providers: '/vector-store/providers',                   // GET - List available providers
    testConnection: '/vector-store/test-connection',        // POST - Test connection
  },

  // Knowledgebase endpoints (RAG pipeline document registry and indexing)
  knowledgeBase: {
    list: '/knowledge-bases-classic',                              // GET - List knowledgebases
    create: '/knowledge-bases-classic',                            // POST - Create knowledgebase
    getById: (id: string) => `/knowledge-bases-classic/${id}`,      // GET - Get by ID
    update: (id: string) => `/knowledge-bases-classic/${id}`,       // PATCH - Update knowledgebase
    delete: (id: string) => `/knowledge-bases-classic/${id}`,       // DELETE - Delete knowledgebase
    stats: (id: string) => `/knowledge-bases-classic/${id}/stats`,  // GET - Get document stats
    workflowDivergence: (id: string) => `/knowledge-bases-classic/${id}/workflow-divergence`,  // GET - form vs workflow divergence
    // Document management
    addDocument: '/knowledge-bases-classic/documents',              // POST - Add document
    addFromNode: '/knowledge-bases-classic/documents/from-node',    // POST - Add from File Upload node
    listDocuments: (kbId: string) => `/knowledge-bases-classic/${kbId}/documents`,  // GET - List documents
    getDocument: (docId: string) => `/knowledge-bases-classic/documents/${docId}`,  // GET - Get document
    updateDocumentStatus: (docId: string) => `/knowledge-bases-classic/documents/${docId}/status`,  // PATCH - Update status
    deleteDocument: (docId: string) => `/knowledge-bases-classic/documents/${docId}`,  // DELETE - Delete document
    // Sync operations
    startSync: '/knowledge-bases-classic/sync',                     // POST - Start sync
    syncHistory: (kbId: string) => `/knowledge-bases-classic/${kbId}/sync-history`,  // GET - Sync history
    syncIncremental: (kbId: string) => `/knowledge-bases-classic/${kbId}/sync/incremental`,  // POST - Incrementally index pending/failed docs
    syncFull: (kbId: string) => `/knowledge-bases-classic/${kbId}/sync/full`,                  // POST - Reset every doc + purge vectors before full re-index
    // Raw-text + URL ingestion (Phase B). Both write the source to GCS as a
    // virtual .txt file and register a kb_documents row with status='pending'
    // so the next Reprocess picks them up via the regular pipeline.
    addRawText: (kbId: string) => `/knowledge-bases-classic/${kbId}/documents/raw-text`,  // POST - Persist raw text as a virtual file
    addUrl: (kbId: string) => `/knowledge-bases-classic/${kbId}/documents/url`,            // POST - Fetch URL + persist extracted text
    // Logo (per-tenant GCS branding/ slot — overwrites on POST)
    logo: (kbId: string) => `/knowledge-bases-classic/${kbId}/logo`,                       // GET serves, POST uploads, DELETE removes
    // History timeline for the About modal (created / updated / processed)
    history: (kbId: string) => `/knowledge-bases-classic/${kbId}/history`,                 // GET
    configVersions: (kbId: string) => `/knowledge-bases-classic/${kbId}/config-versions`,  // GET — settings history (LT-07)
    // Configuration
    embeddingModels: '/knowledge-bases-classic/config/embedding-models',  // GET - List embedding models
  },

  // Vector search endpoints (RAG pipeline retrieval - similarity, hybrid, MMR search)
  vectorSearch: {
    search: '/vector-search/search',                             // POST - Perform vector search
    searchKb: '/vector-search/search-kb',                        // POST - Search knowledgebase by ID
    multiSearch: '/vector-search/multi-search',                  // POST - Search multiple collections
    searchTypes: '/vector-search/search-types',                  // GET - Get available search types
    rerankMethods: '/vector-search/rerank-methods',              // GET - Get available rerank methods
    preview: '/vector-search/preview',                           // POST - Preview search (limited results)
    explain: '/vector-search/explain',                           // POST - Explain search configuration
  },

  // Vector DB nodes endpoints (workflow node operations for Chroma, Pinecone, pgvector, Qdrant)
  vectorDb: {
    // ChromaDB operations
    chromaWrite: '/vector-db/chroma/write',                      // POST - Write to ChromaDB
    chromaSearch: '/vector-db/chroma/search',                    // POST - Search ChromaDB
    chromaDelete: '/vector-db/chroma/delete',                    // POST - Delete from ChromaDB
    // Pinecone operations
    pineconeWrite: '/vector-db/pinecone/write',                  // POST - Write to Pinecone
    pineconeSearch: '/vector-db/pinecone/search',                // POST - Search Pinecone
    pineconeDelete: '/vector-db/pinecone/delete',                // POST - Delete from Pinecone
    // pgvector operations
    pgvectorWrite: '/vector-db/pgvector/write',                  // POST - Write to pgvector
    pgvectorSearch: '/vector-db/pgvector/search',                // POST - Search pgvector
    pgvectorDelete: '/vector-db/pgvector/delete',                // POST - Delete from pgvector
    // Qdrant operations
    qdrantWrite: '/vector-db/qdrant/write',                      // POST - Write to Qdrant
    qdrantSearch: '/vector-db/qdrant/search',                    // POST - Search Qdrant
    qdrantDelete: '/vector-db/qdrant/delete',                    // POST - Delete from Qdrant
    // Common operations
    collectionInfo: '/vector-db/collection-info',                // POST - Get collection info
    providers: '/vector-db/providers',                           // GET - List available providers
  },

  // Graph DB nodes endpoints (Neo4j GraphRAG operations)
  graphDb: {
    // Neo4j operations
    neo4jWrite: '/graph-db/neo4j/write',                         // POST - Write nodes to Neo4j
    neo4jSearch: '/graph-db/neo4j/search',                       // POST - Hybrid vector+graph search
    neo4jDelete: '/graph-db/neo4j/delete',                       // POST - Delete from Neo4j
    neo4jRelationships: '/graph-db/neo4j/relationships',         // POST - Create relationships
    neo4jStats: '/graph-db/neo4j/stats',                         // POST - Get graph statistics
    neo4jTestConnection: '/graph-db/neo4j/test-connection',      // POST - Test connection
    // Entity extraction operations
    entityExtract: '/graph-db/entity-extraction/extract',        // POST - Extract entities from text
  },

  // Node schedules endpoints (background job scheduling for MCP nodes)
  nodeSchedules: {
    list: '/node-schedules',                                      // GET - List schedules (filter by node_id, workflow_id)
    create: '/node-schedules',                                    // POST - Create new schedule
    getById: (id: string) => `/node-schedules/${id}`,             // GET - Get schedule by ID
    update: (id: string) => `/node-schedules/${id}`,              // PATCH - Update schedule
    delete: (id: string) => `/node-schedules/${id}`,              // DELETE - Delete schedule
    pause: (id: string) => `/node-schedules/${id}/pause`,         // POST - Pause schedule
    resume: (id: string) => `/node-schedules/${id}/resume`,       // POST - Resume schedule
    resetCheckpoint: (id: string) => `/node-schedules/${id}/reset-checkpoint`,  // POST - Reset checkpoint
    preview: '/node-schedules/preview',                           // POST - Preview next run times
  },

  // Agent endpoints (AI agent CRUD and execution)
  agents: {
    list: '/agents',                                               // GET - List agents
    create: '/agents',                                             // POST - Create agent
    getById: (id: string) => `/agents/${id}`,                       // GET - Get agent by ID
    update: (id: string) => `/agents/${id}`,                        // PATCH - Update agent
    delete: (id: string) => `/agents/${id}`,                        // DELETE - Soft delete
    run: (id: string) => `/agents/${id}/run`,                       // POST - Execute agent
    publish: (id: string) => `/agents/${id}/publish`,               // POST - Publish agent
    runtimeChat: (id: string) => `/agents/runtime/${id}/chat`,      // POST - Governed chat (tools/guardrails/memory/HITL)
  },

  // Prompt Builder endpoints (LLM-powered structured prompt generation)
  promptBuilder: {
    generate: '/prompt-builder/generate',                          // POST - Generate structured prompt from casual text
    chat: '/prompt-builder/chat',                                  // POST - General chat with UZO AI (Gemini)
    chatSessions: '/prompt-builder/chat/sessions',                 // GET - List general chat sessions
    chatSession: (id: string) => `/prompt-builder/chat/sessions/${id}`,  // GET - Get session, DELETE - Delete session
    createRagPipeline: '/prompt-builder/chat/create-rag-pipeline', // POST - Create single RAG pipeline workflow from chat
    ragPipelines: '/prompt-builder/rag-pipelines',               // GET - List existing RAG pipelines
  },

  // Workflow-scoped RAG chat endpoints (for querying ingested documents)
  chat: {
    direct: '/chat/direct',                                        // POST - Direct RAG chat without session
    directStream: '/chat/direct/stream',                           // POST (SSE) - Direct RAG chat, streamed (sources arrive incrementally; answer arrives as one buffered+gated `token` frame, not token-by-token)
    sessions: '/chat/sessions',                                    // POST (create), GET (list)
    session: (id: string) => `/chat/sessions/${id}`,               // GET - Get session, DELETE - Delete
    sessionMessages: (id: string) => `/chat/sessions/${id}/messages`,  // POST - Send message, GET - History
  },

  // Dataset endpoints (workspace-scoped data assets with classification and security)
  dataset: {
    list: '/datasets',                                           // GET - List datasets
    create: '/datasets',                                         // POST - Create dataset
    update: (id: string) => `/datasets/${id}`,                    // PATCH - Update dataset
    delete: (id: string) => `/datasets/${id}`,                    // DELETE - Soft delete
    getById: (id: string) => `/datasets/${id}`,                   // GET - Get dataset
    classify: (id: string) => `/datasets/${id}/classify`,         // PATCH - Update classification
    security: (id: string) => `/datasets/${id}/security`,         // PATCH - Update row/col security
    schema: (id: string) => `/datasets/${id}/schema`,             // GET - Get schema definition
    preview: (id: string) => `/datasets/${id}/preview`,           // GET - Preview with masking
  },

  // Secret endpoints (versioned secrets with encryption and audit)
  secret: {
    list: '/secrets',                                            // GET - List secrets (metadata only)
    create: '/secrets',                                          // POST - Create secret
    update: (id: string) => `/secrets/${id}`,                     // PATCH - Update secret (new version)
    delete: (id: string) => `/secrets/${id}`,                     // DELETE - Soft delete
    getById: (id: string) => `/secrets/${id}`,                    // GET - Get secret metadata
    getValue: (id: string) => `/secrets/${id}/value`,             // GET - Break-glass secret reveal only; disabled by default
    versions: (id: string) => `/secrets/${id}/versions`,          // GET - List version history
    rotate: (id: string) => `/secrets/${id}/rotate`,              // POST - Rotate secret
    accessLog: (id: string) => `/secrets/${id}/access-log`,       // GET - View audit trail
  },

  // MCP Registry endpoints (service registration, operations, health, audit)
  mcpRegistry: {
    list: '/mcp-registry',                                         // GET - List registered services
    create: '/mcp-registry',                                       // POST - Register new service
    getById: (id: string) => `/mcp-registry/${id}`,                 // GET - Get service by ID
    update: (id: string) => `/mcp-registry/${id}`,                  // PATCH - Update service
    delete: (id: string) => `/mcp-registry/${id}`,                  // DELETE - Delete service
    operations: (id: string) => `/mcp-registry/${id}/operations`,   // GET/PUT - Manage operations
    healthCheck: (id: string) => `/mcp-registry/${id}/health-check`, // POST - Health check single service
    healthSummary: '/mcp-registry/health',                          // GET  - read stored health only
    healthCheckAll: '/mcp-registry/health-check',                   // POST - actually probe every service
    auditLog: '/mcp-registry/audit-log',                            // GET - Global audit log
    serviceAuditLog: (id: string) => `/mcp-registry/${id}/audit-log`, // GET - Service-specific audit log
    discover: (id: string) => `/mcp-registry/${id}/discover`,       // POST - Discover tools (BYOMS)
    testConnection: '/mcp-registry/test-connection',                // POST - Test external connection (real MCP handshake)
    capabilities: (id: string) => `/mcp-registry/${id}/capabilities`, // GET - Last discovery snapshot (protocol, counts, resources)
    readResource: (id: string) => `/mcp-registry/${id}/resources/read`, // POST - Read one resource by URI
  },

  // Billing endpoints (invoices, payment methods, budgets)
  billing: {
    overview: '/billing/overview',
    invoices: '/billing/invoices',
    paymentMethods: '/billing/payment-methods',
    paymentMethodDefault: (id: string) => `/billing/payment-methods/${id}/default`,
    paymentMethodDelete: (id: string) => `/billing/payment-methods/${id}`,
    billingInfo: '/billing/billing-info',
    budgets: '/billing/budgets',
    budgetUpdate: (id: string) => `/billing/budgets/${id}`,
    budgetDelete: (id: string) => `/billing/budgets/${id}`,
    budgetVsActual: '/billing/budget-vs-actual',
  },

  // Knowledge Graph endpoints (Phase 2 — KAG / KG)
  // Backed by api/v1/knowledge_graph.py + services/knowledge_graph.py
  kg: {
    ontology: '/kg/ontology',                                        // GET list, POST upsert
    ontologyEntry: (id: string) => `/kg/ontology/${id}`,             // DELETE
    entities: '/kg/entities',                                        // GET list, POST upsert
    entity: (id: string) => `/kg/entities/${id}`,                    // GET, DELETE
    aliases: (id: string) => `/kg/entities/${id}/aliases`,           // GET, POST
    edgesFor: (id: string) => `/kg/entities/${id}/edges`,            // GET
    edges: '/kg/edges',                                              // POST add
    resolveNl: '/kg/resolve-nl',                                     // POST — NL → entity_id
    traverse: '/kg/traverse',                                        // POST — graph walk
    approvalChain: '/kg/approval-chain',                             // POST — canonical KAG demo
    // Phase 2.5 — sync history (read-only)
    syncHistory: '/kg/sync-history',                                  // GET list
    syncHistoryEntry: (id: string) => `/kg/sync-history/${id}`,       // GET single
    // KIF disambiguation feedback loop — every resolve_nl that returns
    // via_path=none lands here; admin reviews and binds to an entity
    // (which auto-creates an alias so the next call resolves exact).
    unresolvedTerms:        '/kg/unresolved-terms',                                       // GET list
    unresolvedTermBind:     (id: string) => `/kg/unresolved-terms/${id}/bind`,            // POST
    unresolvedTermIgnore:   (id: string) => `/kg/unresolved-terms/${id}/ignore`,          // POST
  },

  // E3 enforce dashboard — guarded-tool shadow analytics over maple.audit_log
  // (event_type='security', event_category='guarded_tool_shadow').
  securityShadow: {
    summary:    '/security/guarded-shadow/summary',     // GET ?days=N&top_n=N
    events:     '/security/guarded-shadow/events',      // GET ?days=N&tool_name&agent_id&limit&offset
    flagStatus: '/security/guarded-shadow/flag-status', // GET
  },

  // HITL endpoints (Phase 3 — multi-layer context surface)
  hitl: {
    inbox: '/hitl/inbox',                                            // GET
    task: (id: string) => `/hitl/tasks/${id}`,                       // GET / PATCH / DELETE
    context: (id: string) => `/hitl/tasks/${id}/context`,            // GET — 4-layer bundle
    complete: (id: string) => `/hitl/tasks/${id}/complete`,          // POST
    escalate: (id: string) => `/hitl/tasks/${id}/escalate`,          // POST
  },

  // Voice endpoints (Phase 4 — voice sessions, slots, resume tokens)
  voice: {
    sessions: '/voice/sessions',                                     // POST create
    resume: '/voice/sessions/resume',                                // POST resume
    session: (id: string) => `/voice/sessions/${id}`,                // GET
    pause: (id: string) => `/voice/sessions/${id}/pause`,            // POST
    complete: (id: string) => `/voice/sessions/${id}/complete`,      // POST
    slots: (id: string) => `/voice/sessions/${id}/slots`,            // POST
    slotConfirm: (sid: string, slotId: string) =>
      `/voice/sessions/${sid}/slots/${slotId}/confirm`,
    slotReject: (sid: string, slotId: string) =>
      `/voice/sessions/${sid}/slots/${slotId}/reject`,
    transcript: (id: string) => `/voice/sessions/${id}/transcript`,
    // Voice P2 — server-side STT/TTS (Govern-gated, tenant OpenAI connector)
    settings: '/voice/settings',                                     // GET (sanitized, workspace-resolved)
    tenantSettings: '/voice/settings/tenant',                        // PUT (tenant.write)
    workspaceSettings: (wsId: string) => `/voice/settings/workspace/${wsId}`,  // PUT
    catalog: '/voice/catalog',                                       // GET the 5 stock voices
    usage: '/voice/usage',                                           // GET spend (cost.read)
    cloneConsent: '/voice/profiles/consent',                         // POST (voice.clone)
    profiles: '/voice/profiles',                                     // GET list · POST create
    profileSample: (id: string) => `/voice/profiles/${id}/sample`,   // POST multipart
    profile: (id: string) => `/voice/profiles/${id}`,                // DELETE
    transcribe: '/voice/transcribe',                                 // POST multipart audio
    tts: '/voice/tts',                                               // POST → audio/mpeg
  },

  // Industry Benchmarks endpoints (Phase 4 — cross-tenant, k≥5, ε-DP)
  benchmarks: {
    metrics: '/benchmarks/metrics',                                  // GET catalog
    aggregates: '/benchmarks/aggregates',                            // GET DP-noised
    settings: '/benchmarks/settings',                                // GET / POST
  },

  // Decisions endpoints (Phase 1 — Context Graph)
  // Backed by api/v1/decisions.py + services/decision_store.py
  // + services/cg_session_store.py (CIF Phase 1 sessions)
  // + services/agent_self_explanation.py (E9 self-explanation)
  // + services/cif_context_injection.py (CIF Phase 2 injection block)
  // + services/multi_layer_context.py (4-layer HITL context)
  decisions: {
    list: '/decisions',                                              // GET
    create: '/decisions',                                            // POST
    getById: (id: string) => `/decisions/${id}`,                     // GET
    delete: (id: string) => `/decisions/${id}`,                      // DELETE (soft, admin)
    similar: '/decisions/similar',                                   // POST
    attachOutcome: (id: string) => `/decisions/${id}/outcome`,       // POST
    listOutcomes: (id: string) => `/decisions/${id}/outcomes`,       // GET
    recomputeDecay: '/decisions/admin/recompute-decay',              // POST (admin)
    // CIF Phase 1 — CG Sessions
    initSession:     '/decisions/sessions',                          // POST
    listSessions:    '/decisions/sessions',                          // GET
    getSession:      (id: string) => `/decisions/sessions/${id}`,    // GET
    updateSlot:      (id: string) => `/decisions/sessions/${id}/slots`, // POST
    getSlot:         (id: string, name: string) =>
                       `/decisions/sessions/${id}/slots/${name}`,    // GET
    missingSlots:    (id: string) =>
                       `/decisions/sessions/${id}/missing-slots`,    // GET
    endSession:      (id: string) =>
                       `/decisions/sessions/${id}/end`,              // POST
    // E9 — Self-explanation
    explainDecision: (id: string) => `/decisions/${id}/explain`,     // POST
    explainSession:  (id: string) =>
                       `/decisions/sessions/${id}/explain`,          // POST
    explainLastForAgent: (agentId: string) =>
                       `/decisions/agents/${agentId}/explain-last`,  // POST
    // CIF Phase 2 — 4-layer HITL panel
    hitlContext:     (taskId: string) =>
                       `/decisions/hitl-tasks/${taskId}/context`,    // GET
  },

  // Environment endpoints (dev/staging/prod pipeline with promotion)
  environment: {
    list: '/environments',                                       // GET - List environments
    create: '/environments',                                     // POST - Create environment
    update: (id: string) => `/environments/${id}`,                // PATCH - Update environment
    delete: (id: string) => `/environments/${id}`,                // DELETE - Soft delete
    getById: (id: string) => `/environments/${id}`,               // GET - Get environment
    seed: (wsId: string) => `/environments/workspace/${wsId}/seed`, // POST - Seed defaults
    promoteWorkflow: (workflowId: string) => `/environments/workflows/${workflowId}/promote`, // POST - Promote workflow
  },
};
